import { Group, PerspectiveCamera, Raycaster, Scene, Vector2, Vector3 } from 'three';

import type { GraphNodeAnchor } from '../../../../core/entities/graph-document';
import type { Scene3DNodePresentation } from './Scene3DNodePresentation';

export interface Scene3DViewport {
	width: number;
	height: number;
	pixelRatio: number;
}

export interface Scene3DNodeSnapshot {
	nodeId: string;
	anchor: GraphNodeAnchor;
	presentation: Scene3DNodePresentation;
}

export interface Scene3DNodeInstance extends Scene3DNodeSnapshot {
	position: Vector3;
}

export interface Scene3DRenderSnapshot {
	focusedNodeId: string | null;
	nodes: Scene3DNodeSnapshot[];
}

export interface Scene3DRendererState {
	scene: Scene;
	camera: PerspectiveCamera;
	nodeLayer: Group;
	edgeLayer: Group;
	pointerNdc: Vector2;
	raycaster: Raycaster;
	viewport: Scene3DViewport;
	snapshot: Scene3DRenderSnapshot;
	instances: Scene3DNodeInstance[];
}

export function createScene3DRendererState(viewport: Partial<Scene3DViewport> = {}): Scene3DRendererState {
	const scene = new Scene();
	const nodeLayer = new Group();
	const edgeLayer = new Group();
	const resolvedViewport = resolveViewport(viewport);
	const camera = new PerspectiveCamera(48, resolvedViewport.width / resolvedViewport.height, 1, 40000);

	camera.position.set(0, 0, 1200);
	camera.lookAt(0, 0, 0);
	scene.add(edgeLayer);
	scene.add(nodeLayer);

	return {
		scene,
		camera,
		nodeLayer,
		edgeLayer,
		pointerNdc: new Vector2(0, 0),
		raycaster: new Raycaster(),
		viewport: resolvedViewport,
		snapshot: { focusedNodeId: null, nodes: [] },
		instances: [],
	};
}

export function resizeScene3DRendererState(
	state: Scene3DRendererState,
	viewport: Partial<Scene3DViewport>,
): Scene3DRendererState {
	const nextViewport = resolveViewport({ ...state.viewport, ...viewport });
	state.camera.aspect = nextViewport.width / nextViewport.height;
	state.camera.updateProjectionMatrix();

	return {
		...state,
		viewport: nextViewport,
	};
}

export function setScene3DRenderSnapshot(
	state: Scene3DRendererState,
	snapshot: Scene3DRenderSnapshot,
): Scene3DRendererState {
	return {
		...state,
		snapshot,
		instances: snapshot.nodes.map((node) => ({
			...node,
			position: worldPointFromAnchor(node.anchor),
		})),
	};
}

export function worldPointFromAnchor(anchor: GraphNodeAnchor): Vector3 {
	return new Vector3(anchor.x, -anchor.y, anchor.z);
}

function resolveViewport(viewport: Partial<Scene3DViewport>): Scene3DViewport {
	return {
		width: Math.max(1, Math.round(viewport.width ?? 1280)),
		height: Math.max(1, Math.round(viewport.height ?? 720)),
		pixelRatio: Math.max(1, viewport.pixelRatio ?? 1),
	};
}